import type { Client } from './types.ts';

/**
 * The Fleet status derivation — pure logic, extracted from FleetView so it's unit-testable
 * without loading the whole component (and its supabase client). go-live-protection §4
 * (Decision B) collapses the two old "LIVE" pills into one `live-protected`: going live always
 * flips is_live, and a promoted app-managed client IS live. The lock badge is driven by the
 * client's real is_live at the render site (FleetView), not by this status — so a transitional
 * promoted-but-not-yet-is_live client (e.g. David pre-migration) reads "LIVE" without a false lock.
 */
export type FleetStatus = 'live-protected' | 'building' | 'in-preview' | 'draft' | 'paused';

// #GR19: the three "any build" flags deriveStatus needs — from the full-table view when
// present, else computed from the (windowed) builds list as a fallback.
export interface StatusFlags {
  hasInflight: boolean;
  hasPromoted: boolean;
  hasPreview: boolean;
}

// Derived, never a stored field. Precedence: an explicit pause wins; then a LIVE client —
// is_live OR promoted, now ONE `live-protected` status (the collapse: going live always flips
// is_live, and a promoted app-managed client IS live); then in-flight work; then a standing
// preview; else nothing has been built yet. Flags aggregate across ALL a client's builds (#GR19).
export function deriveStatus(client: Client, flags: StatusFlags): FleetStatus {
  if (client.status === 'paused') return 'paused';
  if (client.is_live) return 'live-protected';
  if (flags.hasInflight) return 'building';
  if (flags.hasPromoted) return 'live-protected'; // promoted ⇒ live/protected (collapsed; lock badge is is_live-driven)
  if (flags.hasPreview) return 'in-preview';
  return 'draft';
}
